import fetch from 'isomorphic-unfetch';

const DEFAULT_TIMEOUT = 5000;
const RESPONSE_TYPES = {
  TEXT: 'text',
  JSON: 'json',
};

function isValidTimeout(timeout) {
  return timeout > 0;
}

async function handleBadStatusCode(res, dontThrowOnBadStatus) {
  if (dontThrowOnBadStatus) return res;
  if (res && res.status < 400) return res;
  const err = new Error(
    `Request to ${res.url} failed with status ${res.status}`,
  );
  err.status = res.status;
  err.statusText = res.statusText;
  err.response = res.body;
  throw err;
}

async function tryParseResponseBody(res, dontThrowOnBadStatus) {
  let responseReadMethodName = RESPONSE_TYPES.TEXT;
  if (res?.headers?.get) {
    const contentTypeStr = res.headers.get('content-type');
    responseReadMethodName = contentTypeStr && contentTypeStr.toLowerCase().includes('json')
      ? RESPONSE_TYPES.JSON
      : RESPONSE_TYPES.TEXT;
  }

  // NB: a 204-status would imply no body, so res.json will fail. Need to manually return
  // the empty payload
  if (res.status === 204) return {};

  await handleBadStatusCode(res, dontThrowOnBadStatus);

  if (res[responseReadMethodName]) {
    return res[responseReadMethodName]();
  }
  throw new Error('An error occurred in baseFetch tryParseResponseBody');
}

function performRequestSanityCheck(baseUrl, options) {
  if (!options.caller) {
    throw new Error('Option "caller" MUST be supplied to fetch!');
  }
  if (!baseUrl) {
    throw new Error('Cannot fetch empty url');
  }
  if (typeof baseUrl !== 'string') {
    throw new Error('Unable to fetch non-string url');
  }
  if (options.timeoutBreak && !isValidTimeout(options.timeoutBreak)) {
    throw new Error('Invalid timeout value');
  }
}

function getCurrentProtocol() {
  return window?.location?.protocol?.toLowerCase() || 'http:';
}

function setProtocol(url) {
  const urlLowerCase = url.toLowerCase();
  if (urlLowerCase.startsWith('//')) {
    return `${getCurrentProtocol()}${urlLowerCase}`;
  }
  return urlLowerCase;
}

export function baseFetch(baseUrl, options = {}) {
  return new Promise(async (resolve, reject) => {
    try {
      performRequestSanityCheck(baseUrl, options);
    } catch (error) {
      return reject(error);
    }

    const optionsToUse = options;

    const abortController = new AbortController();
    optionsToUse.headers = options.headers || {};
    optionsToUse.headers['x-caller-app'] = options.caller;
    optionsToUse.signal = abortController.signal;
    if (!options.timeoutBreak) optionsToUse.timeoutBreak = DEFAULT_TIMEOUT;

    optionsToUse.timeoutID = setTimeout(() => {
      abortController.abort();
      return reject(new Error('Request timed out'));
    }, options.timeoutBreak);

    try {
      const response = await fetch(setProtocol(baseUrl), optionsToUse);
      if (optionsToUse.timeoutID) clearTimeout(optionsToUse.timeoutID);
      const handledResponse = await tryParseResponseBody(
        response,
        optionsToUse.dontThrowOnBadStatus,
      );
      return resolve(handledResponse);
    } catch (error) {
      return reject(error);
    }
  });
}

export function jsonFetch(url, options = {}) {
  const opts = {
    ...options,
    headers: {
      ...options.headers,
      Accept: options.headers.Accept || 'application/json',
    },
  };
  return baseFetch(url, opts);
}
